/**
 * Standalone widget for CDN / vanilla JS usage.
 * No React dependency — pure DOM manipulation.
 *
 * Usage via CDN:
 *   <script type="module">
 *     import { mount } from 'https://cdn.jsdelivr.net/npm/kanha/dist/widget.js';
 *     const bot = mount('#chat', { modelUrl: '...', systemPrompt: '...' });
 *   </script>
 *
 * Or as a web component:
 *   <script type="module" src="https://cdn.jsdelivr.net/npm/kanha/dist/widget.js"></script>
 *   <kanha-bot model-url="..." system-prompt="..." bot-name="Acme Bot"></kanha-bot>
 */

import type {
  KanhaBotProps,
  KanhaBotTheme,
  KanhaChatMetrics,
  CompletionEvent,
  Message,
  InferenceMode,
  RetrievalEvent,
} from "./types";
import {
  checkAvailableMemory,
  checkWebGPU,
  createEngine,
  describeLoadError,
  describeLoadStage,
  disposeEngine,
  emitMetrics,
  emitRetrieval,
  resetEngine,
  resetEngineBestEffort,
  resolveSystemPrompt,
  retrievalChunks,
  stripThinkTokens,
  usesMiniCPMModel,
  usesPrefilledMiniCPMThinking,
} from "./webllm";
import { formatRagPrompt } from "./rag-prompt";
import { CHAT_CSS, loadChatFonts, rootClassName, themeVariables } from "./chat-theme";
import { escapeHtml, renderMarkdown } from "./markdown";
import { buildEngineHistory } from "./engine-history";
import {
  buildHybridGroundedTurn,
  buildGroundedUserContent,
  fetchGroundedCorpus,
  GROUNDED_NO_MATCH_REPLY,
  groundedSystemPrompt,
  finalizeGroundedAnswer,
  hasContentTerms,
  loadCachedGroundedCorpus,
  prepareGroundedRetrieval,
  type GroundedCorpusCache,
  type GroundedTurn,
  type GroundedCorpus,
  type GroundedSource,
} from "./grounded-corpus";
import type { TransformersModule } from "./transformers-retrieval";
import {
  groundedRetrievalProviders,
  resolvedRetrievalWorkerUrl,
} from "./browser-retrieval";
import type { RetrievalProgress } from "./hybrid-retrieval";
import { modelTokenCounterWithin, warmModelTokenCounter } from "./model-token-counter";
import {
  completionEvent,
  emitCompletion,
  type CompletionEventBase,
} from "./completion-trace";
import {
  failedGenerationMessages,
  GENERATION_ERROR_REPLY,
} from "./generation-failure";
import {
  appendTranscript,
  createVoiceInput,
  voiceInputSupported,
  type VoiceInput,
  type VoiceInputState,
} from "./speech";
import {
  createReadAloud,
  readAloudSupported,
  type ReadAloud,
  type ReadAloudState,
} from "./read-aloud";

declare const __KANHA_WEBLLM_MODULE_URL__: string;
declare const __KANHA_TRANSFORMERS_MODULE_URL__: string;

const WEBLLM_MODULE_URL: string = __KANHA_WEBLLM_MODULE_URL__;
const TRANSFORMERS_MODULE_URL: string = __KANHA_TRANSFORMERS_MODULE_URL__;

function loadTransformers() {
  return import(TRANSFORMERS_MODULE_URL) as unknown as Promise<TransformersModule>;
}

// ---------------------------------------------------------------------------
// DOM Widget
// ---------------------------------------------------------------------------

const CSS = `:host { all: initial; }\n${CHAT_CSS}`;

const SVG_CHAT = `<svg width="SIZE" height="SIZE" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;
const SVG_CLOSE = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
const SVG_SEND = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;
const SVG_MIC = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`;
const SVG_STOP = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`;
const SVG_SPEAKER = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4.7a.7.7 0 0 0-1.2-.5L6.4 7.6A1.4 1.4 0 0 1 5.4 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.4a1.4 1.4 0 0 1 1 .4l3.4 3.4a.7.7 0 0 0 1.2-.5z"/><path d="M16 9a5 5 0 0 1 0 6"/><path d="M19.4 18.4a9 9 0 0 0 0-12.8"/></svg>`;
const SVG_STOP_SMALL = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`;
const SVG_TRASH = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`;
const SVG_SPINNER = `<svg width="SIZE" height="SIZE" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="kb-spinner"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`;

function chatIcon(size: number) {
  return SVG_CHAT.replace(/SIZE/g, String(size));
}

function spinner(size = 18) {
  return SVG_SPINNER.replace(/SIZE/g, String(size));
}

interface WidgetState {
  messages: Message[];
  input: string;
  isLoading: boolean;
  isThinking: boolean;
  mode: InferenceMode;
  loadProgress: number;
  loadStage: string;
  error: string | null;
  isOpen: boolean;
  voiceState: VoiceInputState;
  voiceLoadProgress: number | null;
  readAloudIndex: number | null;
  readAloudState: ReadAloudState;
  readAloudProgress: number | null;
}

class KanhaBotWidget {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private engine: any = null;
  private worker: Worker | null = null;
  private props: KanhaBotProps;
  private state: WidgetState;
  private root: ShadowRoot;
  private stickToBottom = true;
  private justOpened = false;
  private messagesEl: HTMLElement | null = null;
  private textareaEl: HTMLTextAreaElement | null = null;
  private voice: VoiceInput | null = null;
  private readAloud: ReadAloud | null = null;
  private unmounted = false;
  private generation = 0;
  private streamTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingText: string | null = null;
  private pendingSources: GroundedSource[] | undefined = undefined;
  private corpus: GroundedCorpusCache = { current: null };
  private groundedRetrievalAttempt: {
    identity: string;
    generation: number;
    completed: boolean;
  } | null = null;

  constructor(container: HTMLElement, props: KanhaBotProps) {
    this.props = {
      botName: "AI Assistant",
      welcomeMessage: "Ask me anything!",
      suggestions: [],
      theme: {},
      ...props,
    };
    this.state = {
      messages: [],
      input: "",
      isLoading: false,
      isThinking: false,
      mode: "detecting",
      loadProgress: 0,
      loadStage: "Loading model",
      error: null,
      isOpen: false,
      voiceState: "idle",
      voiceLoadProgress: null,
      readAloudIndex: null,
      readAloudState: "idle",
      readAloudProgress: null,
    };
    if (this.props.voiceInput !== false && voiceInputSupported()) {
      this.voice = createVoiceInput({
        config: () => this.props,
        onChange: ({ state, loadProgress }) => this.setState({
          voiceState: state,
          voiceLoadProgress: loadProgress,
          ...(state === "recording" ? { error: null } : {}),
        }),
        onTranscript: (text) => this.setState({ input: appendTranscript(this.state.input, text) }),
        onError: (error) => this.setState({ error }),
      });
    }
    if (this.props.readAloud !== false && readAloudSupported()) {
      this.readAloud = createReadAloud({
        config: () => this.props,
        onChange: ({ messageIndex, state, loadProgress }) => this.setState({
          readAloudIndex: messageIndex,
          readAloudState: state,
          readAloudProgress: loadProgress,
          ...(state === "preparing" ? { error: null } : {}),
        }),
        onError: (error) => this.setState({ error }),
      });
    }
    if (this.props.theme?.loadFonts !== false) loadChatFonts();
    this.root = container.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = CSS;
    this.root.appendChild(style);
    this.render();
    this.initEngine();
  }

  private get position(): "bottom-right" | "bottom-left" {
    return this.props.theme?.position ?? "bottom-right";
  }

  private async initEngine() {
    const hasWebGPU = await checkWebGPU();
    if (this.unmounted) return;
    if (!hasWebGPU) {
      this.setState({ mode: "error", error: "WebGPU is not supported in this browser. Try Chrome 113+ or Edge 113+." });
      return;
    }

    const memWarning = checkAvailableMemory(this.props);
    if (memWarning) {
      this.setState({ mode: "error", error: memWarning });
      return;
    }


    this.setState({ mode: "loading" });
    try {
      const webllm = await import(WEBLLM_MODULE_URL);
      const result = await createEngine(
        webllm,
        this.props,
        (report) => {
          if (this.unmounted) return;
          this.setState({
            loadProgress: Math.round(report.progress * 100),
            loadStage: describeLoadStage(report.text),
          });
        },
      );

      if (this.unmounted) {
        await disposeEngine(result.engine, result.worker);
        return;
      }
      this.engine = result.engine;
      this.worker = result.worker;

      try {
        console.info("[WebLLM] Warming up shaders...");
        await this.engine.chat.completions.create({
          messages: [{ role: "user", content: "warmup" }],
          max_tokens: 1,
        });
        console.info("[WebLLM] Warmup complete.");
      } catch (e) {
        console.warn("[WebLLM] Warmup failed", e);
      }

      this.setState({ mode: "ready" });
      this.warmRetrieval();
    } catch (err) {
      const message = await describeLoadError(err, this.props);
      if (!this.unmounted) {
        this.setState({ mode: "error", error: message });
      }
    }
  }

  private setState(partial: Partial<WidgetState>) {
    if (this.unmounted) return;
    Object.assign(this.state, partial);
    this.render();
  }

  private flushAssistantMessage() {
    if (this.streamTimer) clearTimeout(this.streamTimer);
    this.streamTimer = null;
    const text = this.pendingText;
    this.pendingText = null;
    if (!text) return;
    const cleaned = stripThinkTokens(text);
    if (!cleaned) {
      this.setState({ isThinking: true });
      return;
    }
    const messages = [...this.state.messages];
    const message: Message = { role: "assistant", content: cleaned, sources: this.pendingSources };
    if (messages[messages.length - 1]?.role === "assistant") {
      messages[messages.length - 1] = message;
    } else {
      messages.push(message);
    }
    this.setState({ messages, isThinking: false });
  }

  private warmRetrieval() {
    const url = this.props.ragCorpusUrl;
    if (!url || this.props.onRetrieveContext) return;
    if (this.props.modelUrl) void warmModelTokenCounter(this.props.modelUrl, loadTransformers);
    void this.loadCorpus(url)
      .then((corpus) => prepareGroundedRetrieval({
        corpus,
        ...groundedRetrievalProviders(this.props),
        onProgress: this.props.onRetrievalProgress,
      }))
      .catch((err) => console.warn("[KanhaBot] Retrieval warm-up failed:", err));
  }

  private loadCorpus(url: string): Promise<GroundedCorpus> {
    return loadCachedGroundedCorpus(this.corpus, url, fetchGroundedCorpus);
  }

  private updateAssistantMessage(text: string) {
    this.pendingText = text;
    if (!this.streamTimer) {
      this.streamTimer = setTimeout(
        () => this.flushAssistantMessage(),
        this.props.streamUpdateIntervalMs ?? 50,
      );
    }
  }

  public setProp<K extends keyof KanhaBotProps>(key: K, value: KanhaBotProps[K]) {
    this.props[key] = value;
  }

  private async handleSend() {
    const text = this.state.input.trim();
    if (!text || this.state.mode !== "ready" || this.state.isLoading || !this.engine) return;

    this.readAloud?.stop();
    const userMsg: Message = { role: "user", content: text };
    const allMessages = [...this.state.messages, userMsg];
    const generation = this.generation + 1;
    this.generation = generation;
    this.stickToBottom = true;
    this.setState({ messages: allMessages, input: "", isLoading: true, isThinking: true, error: null });

    let completionBase: CompletionEventBase | null = null;
    let rawModelCompletion = "";
    let firstGroundedRetrievalAttempt: {
      identity: string;
      generation: number;
      completed: boolean;
    } | null = null;
    try {
      let systemPrompt = resolveSystemPrompt(this.props);
      let engineUserContent = text;
      let groundedTemperature: number | null = null;
      let groundedTurn: GroundedTurn | null = null;
      let groundedPromptMessages: Message[] | null = null;
      let ragTime = 0;
      let ragContextLength = 0;
      this.pendingSources = undefined;

      if (this.props.onRetrieveContext) {
        try {
          const ragStartTime = performance.now();
          const context = await this.props.onRetrieveContext(text);
          ragTime = (performance.now() - ragStartTime) / 1000;

          if (context) {
            ragContextLength = context.length;
            engineUserContent = formatRagPrompt(context, text, this.props.ragPromptTemplate);
          }
        } catch (err) {
          console.error("[KanhaBot] RAG Retrieval Failed:", err);
        }
      } else if (this.props.ragCorpusUrl) {
        const ragStartTime = performance.now();
        let corpus: GroundedCorpus | null = null;
        try {
          corpus = await this.loadCorpus(this.props.ragCorpusUrl);
        } catch (err) {
          console.error("[KanhaBot] Grounding corpus failed to load:", err);
          throw err;
        }
        ragTime = (performance.now() - ragStartTime) / 1000;

        if (this.generation !== generation) return;
        if (corpus && !hasContentTerms(text)) {
          emitRetrieval(this.props, {
            query: text,
            decision: "greeting",
            chunks: [],
            contextLength: 0,
          });
        } else if (corpus) {
          const retrievalIdentity = JSON.stringify([
            this.props.ragRetrievalMode || "hybrid",
            this.props.ragCorpusUrl,
            this.props.ragEmbeddingModel,
            this.props.ragRerankerModel,
            resolvedRetrievalWorkerUrl(this.props.retrievalWorkerUrl),
          ]);
          const readyWaitMs = this.groundedRetrievalAttempt?.identity === retrievalIdentity
            ? undefined
            : 10_000;
          if (readyWaitMs) {
            firstGroundedRetrievalAttempt = {
              identity: retrievalIdentity,
              generation,
              completed: false,
            };
            this.groundedRetrievalAttempt = firstGroundedRetrievalAttempt;
          }
          const { semantic, reranker } = groundedRetrievalProviders(
            this.props,
            readyWaitMs,
          );
          this.props.onRetrievalProgress?.({
            stage: "embedding",
            progress: 0,
            text: "Preparing prompt budget",
          });
          const tokenCounter = this.props.modelUrl
            ? await modelTokenCounterWithin(this.props.modelUrl, loadTransformers)
            : undefined;
          const hybrid = await buildHybridGroundedTurn({
            corpus,
            query: text,
            semantic,
            reranker,
            isCancelled: () => this.generation !== generation,
            onProgress: this.props.onRetrievalProgress,
            contextWindowSize: this.props.contextWindowSize,
            maxOutputTokens: this.props.maxTokens,
            tokenCounter,
            history: allMessages.slice(0, -1),
            maxHistoryMessages: this.props.maxHistoryMessages,
          });
          if (firstGroundedRetrievalAttempt) firstGroundedRetrievalAttempt.completed = true;
          if (this.generation !== generation) return;
          const grounded = hybrid.turn;
          if (!grounded) {
            emitRetrieval(this.props, {
              query: text,
              decision: "no_match",
              chunks: [],
              contextLength: 0,
              diagnostics: hybrid.diagnostics,
            });
            this.setState({
              messages: [...allMessages, { role: "assistant", content: GROUNDED_NO_MATCH_REPLY }],
            });
            return;
          }
          emitRetrieval(this.props, {
            query: text,
            decision: "grounded",
            chunks: retrievalChunks(grounded.chunks),
            contextLength: grounded.context.length,
            context: grounded.context,
            systemPrompt: groundedSystemPrompt(),
            userPrompt: buildGroundedUserContent(grounded.context, text),
            promptMessages: hybrid.promptMessages,
            diagnostics: hybrid.diagnostics,
          });
          groundedTurn = grounded;
          groundedPromptMessages = hybrid.promptMessages;
          ragContextLength = grounded.context.length;
          engineUserContent = buildGroundedUserContent(grounded.context, text);
          systemPrompt = groundedSystemPrompt();
          groundedTemperature = 0;
        }
      }

      if (this.generation !== generation) return;

      const engineMessages = groundedPromptMessages ?? [
        ...(systemPrompt ? [{ role: "system" as const, content: systemPrompt }] : []),
        ...buildEngineHistory(allMessages, this.props.maxHistoryMessages, engineUserContent),
      ];
      const prefilledMiniCPMThinking = usesPrefilledMiniCPMThinking(this.props);
      if (usesMiniCPMModel(this.props)) {
        await resetEngine(this.engine);
        if (this.generation !== generation) return;
      }

      const startTime = performance.now();
      const startedAt = new Date().toISOString();
      let firstTokenTime: number | null = null;
      let contentChunkCount = 0;
      let completionTokens: number | null = null;
      const generationSettings = {
        temperature: groundedTemperature ?? this.props.temperature ?? 0.7,
        topP: this.props.topP ?? 0.8,
        repetitionPenalty: this.props.repetitionPenalty ?? 1.1,
        maxTokens: this.props.maxTokens ?? 1024,
        enableThinking: this.props.enableThinking ?? false,
        stream: true as const,
        includeUsage: true as const,
      };
      completionBase = {
        modelUrl: this.props.modelUrl,
        modelLib: this.props.modelLib,
        modelSize: this.props.modelSize,
        promptMessages: engineMessages,
        generationSettings,
        startedAt,
      };

      const completion = await this.engine.chat.completions.create({
        messages: engineMessages,
        temperature: generationSettings.temperature,
        top_p: generationSettings.topP,
        repetition_penalty: generationSettings.repetitionPenalty,
        max_tokens: generationSettings.maxTokens,
        extra_body: {
          enable_thinking: generationSettings.enableThinking,
          enable_latency_breakdown: true
        },
        stream: generationSettings.stream,
        stream_options: { include_usage: generationSettings.includeUsage },
      });

      for await (const chunk of completion) {
        if (this.generation !== generation) break;
        const delta = chunk.choices[0]?.delta?.content || "";
        if (delta) {
          if (firstTokenTime === null) firstTokenTime = performance.now();
          contentChunkCount += 1;
          rawModelCompletion += delta;
          this.updateAssistantMessage(stripThinkTokens(rawModelCompletion, prefilledMiniCPMThinking));
        }
        if (chunk.usage) {
          completionTokens = chunk.usage.completion_tokens;
          emitMetrics(chunk.usage, this.props);
        }
      }
      if (this.generation === generation) {
        let displayedAnswer = stripThinkTokens(rawModelCompletion, prefilledMiniCPMThinking);
        if (groundedTurn) {
          const finalized = finalizeGroundedAnswer(
            displayedAnswer,
            groundedTurn,
            text,
          );
          this.pendingSources = finalized.sources;
          this.pendingText = finalized.content;
          displayedAnswer = finalized.content;
        }
        this.flushAssistantMessage();
        emitCompletion(this.props, completionEvent(
          completionBase,
          "success",
          rawModelCompletion,
          displayedAnswer,
        ));
        const endTime = performance.now();
        try {
          if (this.engine.runtimeStatsText) {
            const stats = await this.engine.runtimeStatsText();
            const outputTokens = completionTokens ?? contentChunkCount;
            const ttft = firstTokenTime ? (firstTokenTime - startTime) / 1000 : 0;
            const decodeTime = firstTokenTime ? (endTime - firstTokenTime) / 1000 : 0;
            const tps = decodeTime > 0 ? Math.max(0, outputTokens - 1) / decodeTime : 0;
            const totalTime = (endTime - startTime) / 1000;

            const ragStatsLog = ragContextLength > 0
              ? `RAG Retrieval Time: ${ragTime.toFixed(3)}s\nRAG Context Inserted: ${ragContextLength} characters\n`
              : "";

            console.info(
              `[WebLLM Metrics]\n` +
              `${stats}\n` +
              ragStatsLog +
              `TTFT (Time To First Token): ${ttft.toFixed(3)}s\n` +
              `App-Level Decode Speed: ${tps.toFixed(2)} tok/s\n` +
              `Total Request Time: ${totalTime.toFixed(3)}s\n` +
              `Output Tokens: ${outputTokens}`,
            );
          }
        } catch {
        }
      }
    } catch (error) {
      if (this.generation === generation) {
        if (this.streamTimer) clearTimeout(this.streamTimer);
        this.streamTimer = null;
        this.pendingText = null;
        this.pendingSources = undefined;
        if (completionBase) {
          emitCompletion(this.props, completionEvent(
            completionBase,
            "error",
            rawModelCompletion,
            GENERATION_ERROR_REPLY,
            error,
          ));
        }
        this.setState({
          error: "Failed to generate response. Please try again.",
          messages: failedGenerationMessages(this.state.messages),
        });
      }
    } finally {
      if (
        firstGroundedRetrievalAttempt
        && !firstGroundedRetrievalAttempt.completed
        && this.generation !== generation
        && this.groundedRetrievalAttempt === firstGroundedRetrievalAttempt
      ) {
        this.groundedRetrievalAttempt = null;
      }
      if (this.generation === generation) {
        this.flushAssistantMessage();
        this.setState({ isLoading: false, isThinking: false });
      }
    }
  }

  private render() {
    const {
      messages,
      isLoading,
      isThinking,
      mode,
      loadProgress,
      loadStage,
      error,
      isOpen,
      voiceState,
      voiceLoadProgress,
      readAloudIndex,
      readAloudState,
      readAloudProgress,
    } = this.state;
    const isReady = mode === "ready";
    const isVoiceBusy = voiceState !== "idle";
    const loadingLabel = `${loadStage} (${loadProgress}%)`;
    const botName = this.props.botName!;
    const previousScroll = this.messagesEl?.scrollTop ?? 0;

    const style = this.root.querySelector("style");
    this.root.innerHTML = "";
    if (style) this.root.appendChild(style);
    this.messagesEl = null;

    const wrapper = document.createElement("div");
    wrapper.className = rootClassName(this.position, this.props.theme?.appearance);
    for (const [name, value] of Object.entries(themeVariables(this.props.theme?.primaryColor))) {
      wrapper.style.setProperty(name, value);
    }
    this.root.appendChild(wrapper);

    if (isOpen) {
      const panel = document.createElement("div");
      panel.className = this.justOpened ? "kb-panel kb-panel-enter" : "kb-panel";
      this.justOpened = false;
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-label", botName);
      panel.onkeydown = (e) => {
        if (e.key === "Escape") this.close();
      };
      wrapper.appendChild(panel);

      const header = document.createElement("div");
      header.className = "kb-header";
      header.innerHTML = `
        <div class="kb-avatar" aria-hidden="true">${chatIcon(16)}</div>
        <div class="kb-header-text">
          <p class="kb-header-title">${escapeHtml(botName)}</p>
          <p class="kb-header-sub">${
            isReady ? "Runs on-device in your browser"
              : mode === "loading" ? escapeHtml(loadingLabel)
              : mode === "error" ? "Could not load"
              : "Checking your browser..."
          }</p>
        </div>
        <div class="kb-header-actions">
          ${messages.length > 0 ? `<button type="button" class="kb-icon-btn" data-action="clear" aria-label="Clear chat">${SVG_TRASH}</button>` : ""}
          <button type="button" class="kb-icon-btn" data-action="close" aria-label="Close chat">${SVG_CLOSE}</button>
        </div>`;
      header.querySelector("[data-action=clear]")?.addEventListener("click", () => this.clear());
      header.querySelector("[data-action=close]")?.addEventListener("click", () => this.close());
      panel.appendChild(header);

      if (mode === "loading") {
        const progress = document.createElement("div");
        progress.className = "kb-progress";
        progress.innerHTML = `<div class="kb-progress-fill" style="width:${loadProgress}%"></div>`;
        panel.appendChild(progress);
      }

      const msgsContainer = document.createElement("div");
      msgsContainer.className = "kb-messages";
      msgsContainer.setAttribute("aria-live", "polite");
      msgsContainer.onscroll = () => {
        const distance = msgsContainer.scrollHeight - msgsContainer.scrollTop - msgsContainer.clientHeight;
        this.stickToBottom = distance < 48;
      };
      panel.appendChild(msgsContainer);

      if (messages.length === 0) {
        const empty = document.createElement("div");
        empty.className = "kb-empty";
        if (isReady) {
          empty.innerHTML = `<div class="kb-empty-mark" aria-hidden="true">${chatIcon(22)}</div><p class="kb-empty-title">${escapeHtml(botName)}</p><p>${escapeHtml(this.props.welcomeMessage!)}</p>`;
        } else if (mode === "loading") {
          empty.innerHTML = `${spinner(28)}<p>${escapeHtml(loadingLabel)}</p><p class="kb-empty-hint">The first load can take a minute. Later visits are instant.</p>`;
        } else if (mode === "error") {
          empty.innerHTML = `<p class="kb-empty-error">${escapeHtml(error ?? "Something went wrong")}</p>`;
        } else {
          empty.innerHTML = `${spinner(28)}<p>Checking your browser...</p>`;
        }
        msgsContainer.appendChild(empty);
      } else {
        const thread = document.createElement("div");
        thread.className = "kb-thread";
        for (const [index, msg] of messages.entries()) {
          if (msg.role === "user") {
            const row = document.createElement("div");
            row.className = "kb-user";
            const bubble = document.createElement("p");
            bubble.className = "kb-user-bubble";
            bubble.textContent = msg.content;
            row.appendChild(bubble);
            thread.appendChild(row);
            continue;
          }
          const turn = document.createElement("div");
          turn.className = "kb-bot";
          const body = document.createElement("div");
          body.className = "kb-md";
          body.innerHTML = renderMarkdown(msg.content);
          turn.appendChild(body);
          if (msg.sources && msg.sources.length > 0) {
            const list = document.createElement("ul");
            list.className = "kb-sources";
            list.innerHTML = `<li class="kb-sources-label">Sources</li>`;
            for (const source of msg.sources) {
              const item = document.createElement("li");
              const link = document.createElement("a");
              link.className = "kb-source";
              link.href = source.url;
              link.target = "_blank";
              link.rel = "noopener";
              link.textContent = source.title;
              link.title = source.url;
              item.appendChild(link);
              list.appendChild(item);
            }
            turn.appendChild(list);
          }
          const streaming = isLoading && index === messages.length - 1;
          if (this.readAloud && !streaming) {
            const active = readAloudIndex === index && readAloudState !== "idle";
            const actions = document.createElement("div");
            actions.className = "kb-actions";
            const readBtn = document.createElement("button");
            readBtn.type = "button";
            readBtn.className = active ? "kb-action kb-action-active" : "kb-action";
            const label = !active
              ? "Read aloud"
              : readAloudState === "preparing"
                ? readAloudProgress !== null ? `Preparing voice (${readAloudProgress}%)` : "Preparing voice"
                : "Stop reading";
            readBtn.innerHTML = !active
              ? SVG_SPEAKER
              : readAloudState === "preparing"
                ? `${spinner(14)}<span>${escapeHtml(label)}</span>`
                : `${SVG_STOP_SMALL}<span>Stop</span>`;
            readBtn.setAttribute("aria-label", label);
            readBtn.setAttribute("aria-pressed", String(active));
            readBtn.title = label;
            readBtn.onclick = () => this.readAloud?.toggle(index, msg.content);
            actions.appendChild(readBtn);
            turn.appendChild(actions);
          }
          thread.appendChild(turn);
        }

        if (isLoading && (isThinking || messages[messages.length - 1]?.role !== "assistant")) {
          const turn = document.createElement("div");
          turn.className = "kb-bot";
          turn.innerHTML = `<div class="kb-thinking" role="status"><span class="kb-dots" aria-hidden="true"><span class="kb-dot"></span><span class="kb-dot"></span><span class="kb-dot"></span></span><span class="kb-thinking-label">${isThinking ? "Thinking" : "Responding"}</span></div>`;
          thread.appendChild(turn);
        }
        msgsContainer.appendChild(thread);
      }
      this.messagesEl = msgsContainer;

      const suggestions = this.props.suggestions ?? [];
      if (messages.length === 0 && isReady && suggestions.length > 0) {
        const row = document.createElement("div");
        row.className = "kb-suggestions";
        for (const text of suggestions) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "kb-suggestion";
          btn.textContent = text;
          btn.disabled = isLoading || isVoiceBusy;
          btn.onclick = () => {
            this.setState({ input: text });
            setTimeout(() => this.handleSend(), 0);
          };
          row.appendChild(btn);
        }
        panel.appendChild(row);
      }

      if (error && mode !== "error") {
        const status = document.createElement("p");
        status.className = "kb-status kb-status-error";
        status.setAttribute("role", "alert");
        status.textContent = error;
        panel.appendChild(status);
      }

      const composer = document.createElement("form");
      composer.className = "kb-composer";
      composer.onsubmit = (e) => {
        e.preventDefault();
        this.handleSend();
      };
      panel.appendChild(composer);

      const textarea = document.createElement("textarea");
      textarea.className = "kb-textarea";
      textarea.rows = 1;
      textarea.setAttribute("aria-label", `Ask ${botName} a question`);
      textarea.placeholder = voiceState === "recording"
        ? "Listening, tap the mic when you're done"
        : voiceState === "transcribing"
          ? voiceLoadProgress !== null ? `Preparing voice input (${voiceLoadProgress}%)` : "Transcribing..."
          : isReady ? `Ask ${botName} anything...` : "Waiting for model...";
      textarea.disabled = !isReady || isLoading || isVoiceBusy;
      textarea.value = this.state.input;
      composer.appendChild(textarea);
      this.textareaEl = textarea;

      if (this.voice) {
        const micBtn = document.createElement("button");
        micBtn.type = "button";
        micBtn.className = voiceState === "recording" ? "kb-mic kb-mic-active" : "kb-mic";
        micBtn.disabled = !isReady || isLoading || voiceState === "transcribing";
        micBtn.innerHTML = voiceState === "transcribing"
          ? spinner(18)
          : voiceState === "recording" ? SVG_STOP : SVG_MIC;
        micBtn.setAttribute("aria-label", voiceState === "recording" ? "Stop recording" : "Speak your message");
        micBtn.setAttribute("aria-pressed", String(voiceState === "recording"));
        micBtn.onclick = () => this.voice?.toggle();
        composer.appendChild(micBtn);
      }

      const sendBtn = document.createElement("button");
      if (isLoading) {
        sendBtn.type = "button";
        sendBtn.className = "kb-send kb-send-stop";
        sendBtn.textContent = "Stop";
        sendBtn.setAttribute("aria-label", "Stop generating");
        sendBtn.onclick = () => this.stop();
      } else {
        sendBtn.type = "submit";
        sendBtn.className = "kb-send";
        sendBtn.textContent = "Ask";
        sendBtn.setAttribute("aria-label", "Send message");
        sendBtn.disabled = !isReady || isVoiceBusy || !this.state.input.trim();
      }
      composer.appendChild(sendBtn);

      const resize = () => {
        textarea.style.height = "";
        if (textarea.value) textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
      };
      textarea.oninput = () => {
        this.state.input = textarea.value;
        if (!isLoading) sendBtn.disabled = !isReady || isVoiceBusy || !textarea.value.trim();
        resize();
      };
      textarea.onkeydown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
          e.preventDefault();
          this.handleSend();
        }
      };

      msgsContainer.scrollTop = this.stickToBottom ? msgsContainer.scrollHeight : previousScroll;

      requestAnimationFrame(() => {
        resize();
        if (isReady && !isLoading && !isVoiceBusy && this.textareaEl === textarea) {
          textarea.focus({ preventScroll: true });
          textarea.setSelectionRange(textarea.value.length, textarea.value.length);
        }
      });
    }

    const fab = document.createElement("button");
    fab.type = "button";
    fab.className = isOpen ? "kb-fab kb-fab-open" : "kb-fab";
    fab.innerHTML = isOpen ? SVG_CLOSE : chatIcon(24);
    fab.setAttribute("aria-label", isOpen ? "Close chat" : `Chat with ${botName}`);
    fab.setAttribute("aria-expanded", String(isOpen));
    fab.onclick = () => (isOpen ? this.close() : this.open());
    wrapper.appendChild(fab);
  }

  private open() {
    this.stickToBottom = true;
    this.justOpened = true;
    this.setState({ isOpen: true });
  }

  private close() {
    this.voice?.cancel();
    this.readAloud?.stop();
    this.setState({ isOpen: false });
  }

  stop() {
    const stoppedGeneration = this.generation;
    this.generation += 1;
    if (
      this.groundedRetrievalAttempt?.generation === stoppedGeneration
      && !this.groundedRetrievalAttempt.completed
    ) {
      this.groundedRetrievalAttempt = null;
    }
    try {
      this.engine?.interruptGenerate();
    } catch {
    }
    this.flushAssistantMessage();
    this.setState({ isLoading: false, isThinking: false });
  }

  clear() {
    this.stop();
    this.voice?.cancel();
    this.readAloud?.stop();
    if (this.engine) void resetEngineBestEffort(this.engine);
    this.groundedRetrievalAttempt = null;
    this.setState({ messages: [], error: null });
  }

  destroy() {
    this.stop();
    this.voice?.dispose();
    this.voice = null;
    this.readAloud?.dispose();
    this.readAloud = null;
    this.unmounted = true;
    if (this.streamTimer) clearTimeout(this.streamTimer);
    this.streamTimer = null;
    this.pendingText = null;
    const engine = this.engine;
    const worker = this.worker;
    this.engine = null;
    this.worker = null;
    if (engine) {
      void disposeEngine(engine, worker);
    } else {
      worker?.terminate();
    }
    this.root.innerHTML = "";
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Mount a Kanha chatbot widget onto a DOM element.
 *
 * @param target - CSS selector string or HTMLElement
 * @param props - Bot configuration
 * @returns Controls for stopping, clearing, and destroying the widget
 */
export function mount(
  target: string | HTMLElement,
  props: KanhaBotProps,
): { stop: () => void; clear: () => void; destroy: () => void } {
  const el = typeof target === "string" ? document.querySelector<HTMLElement>(target) : target;
  if (!el) throw new Error(`Kanha: element "${target}" not found`);
  const widget = new KanhaBotWidget(el, props);
  return {
    stop: () => widget.stop(),
    clear: () => widget.clear(),
    destroy: () => widget.destroy(),
  };
}

// ---------------------------------------------------------------------------
// Web Component: <kanha-bot>
// ---------------------------------------------------------------------------

class KanhaBotElement extends HTMLElement {
  private widget: KanhaBotWidget | null = null;
  private _onRetrieveContext?: (query: string) => Promise<string | null>;
  private _ragPromptTemplate?: string;
  private _onRetrieval?: (event: RetrievalEvent) => void;
  private _onRetrievalProgress?: (progress: RetrievalProgress) => void;
  private _onCompletion?: (event: CompletionEvent) => void;

  get onCompletion() {
    return this._onCompletion;
  }

  set onCompletion(fn: ((event: CompletionEvent) => void) | undefined) {
    this._onCompletion = fn;
    if (this.widget) {
      this.widget.setProp("onCompletion", fn);
    }
  }

  get onRetrievalProgress() {
    return this._onRetrievalProgress;
  }

  set onRetrievalProgress(fn: ((progress: RetrievalProgress) => void) | undefined) {
    this._onRetrievalProgress = fn;
    if (this.widget) {
      this.widget.setProp("onRetrievalProgress", fn);
    }
  }

  get onRetrieval() {
    return this._onRetrieval;
  }

  set onRetrieval(fn: ((event: RetrievalEvent) => void) | undefined) {
    this._onRetrieval = fn;
    if (this.widget) {
      this.widget.setProp("onRetrieval", fn);
    }
  }

  get onRetrieveContext() {
    return this._onRetrieveContext;
  }

  set onRetrieveContext(fn: ((query: string) => Promise<string | null>) | undefined) {
    this._onRetrieveContext = fn;
    if (this.widget) {
      this.widget.setProp("onRetrieveContext", fn);
    }
  }

  get ragPromptTemplate() {
    return this._ragPromptTemplate;
  }

  set ragPromptTemplate(val: string | undefined) {
    this._ragPromptTemplate = val;
    if (this.widget) {
      this.widget.setProp("ragPromptTemplate", val);
    }
  }

  connectedCallback() {
    const props: KanhaBotProps = {
      modelUrl: this.getAttribute("model-url") ?? undefined,
      modelLib: this.getAttribute("model-lib") ?? undefined,
      modelSize: (this.getAttribute("model-size") as KanhaBotProps["modelSize"]) ?? undefined,
      systemPrompt: this.getAttribute("system-prompt") ?? undefined,
      temperature: this.hasAttribute("temperature") ? Number(this.getAttribute("temperature")) : undefined,
      topP: this.hasAttribute("top-p") ? Number(this.getAttribute("top-p")) : undefined,
      repetitionPenalty: this.hasAttribute("repetition-penalty")
        ? Number(this.getAttribute("repetition-penalty"))
        : undefined,
      enableThinking: this.hasAttribute("thinking")
        ? this.getAttribute("thinking") !== "false"
        : this.hasAttribute("enable-thinking") || undefined,
      maxTokens: this.hasAttribute("max-tokens") ? Number(this.getAttribute("max-tokens")) : undefined,
      maxHistoryMessages: this.hasAttribute("max-history-messages")
        ? Number(this.getAttribute("max-history-messages"))
        : undefined,
      streamUpdateIntervalMs: this.hasAttribute("stream-update-interval-ms")
        ? Number(this.getAttribute("stream-update-interval-ms"))
        : undefined,
      cacheBackend: (this.getAttribute("cache-backend") as KanhaBotProps["cacheBackend"]) ?? undefined,
      workerUrl: this.getAttribute("worker-url") ?? undefined,
      retrievalWorkerUrl: this.getAttribute("retrieval-worker-url") ?? undefined,
      voiceInput: this.hasAttribute("voice-input")
        ? this.getAttribute("voice-input") !== "false"
        : undefined,
      speechModel: this.getAttribute("speech-model") ?? undefined,
      speechWorkerUrl: this.getAttribute("speech-worker-url") ?? undefined,
      readAloud: this.hasAttribute("read-aloud")
        ? this.getAttribute("read-aloud") !== "false"
        : undefined,
      readAloudModel: this.getAttribute("read-aloud-model") ?? undefined,
      readAloudVoice: this.getAttribute("read-aloud-voice") ?? undefined,
      readAloudLanguage: (this.getAttribute("read-aloud-language") as KanhaBotProps["readAloudLanguage"]) ?? undefined,
      contextWindowSize: this.hasAttribute("context-window-size")
        ? Number(this.getAttribute("context-window-size"))
        : undefined,
      minRamGb: this.hasAttribute("min-ram-gb") ? Number(this.getAttribute("min-ram-gb")) : undefined,
      botName: this.getAttribute("bot-name") ?? undefined,
      welcomeMessage: this.getAttribute("welcome-message") ?? undefined,
      suggestions: this.hasAttribute("suggestions")
        ? JSON.parse(this.getAttribute("suggestions")!)
        : undefined,
      theme: {
        primaryColor: this.getAttribute("primary-color") ?? undefined,
        position: (this.getAttribute("position") as KanhaBotTheme["position"]) ?? undefined,
        appearance: (this.getAttribute("appearance") as KanhaBotTheme["appearance"]) ?? undefined,
        loadFonts: this.hasAttribute("load-fonts") ? this.getAttribute("load-fonts") !== "false" : undefined,
      },
      onRetrieveContext: this._onRetrieveContext,
      ragPromptTemplate: this.getAttribute("rag-prompt-template") ?? this._ragPromptTemplate,
      ragCorpusUrl: this.getAttribute("rag-corpus-url") ?? undefined,
      ragRetrievalMode: (this.getAttribute("rag-retrieval-mode") as KanhaBotProps["ragRetrievalMode"]) ?? undefined,
      ragEmbeddingModel: this.getAttribute("rag-embedding-model") ?? undefined,
      ragRerankerModel: this.getAttribute("rag-reranker-model") ?? undefined,
      onRetrieval: this._onRetrieval,
      onRetrievalProgress: this._onRetrievalProgress,
      onCompletion: this._onCompletion,
    };
    this.widget = new KanhaBotWidget(this, props);
  }

  disconnectedCallback() {
    this.widget?.destroy();
  }
}

if (typeof customElements !== "undefined" && !customElements.get("kanha-bot")) {
  customElements.define("kanha-bot", KanhaBotElement);
}

export type { KanhaBotProps, KanhaBotTheme, KanhaChatMetrics, Message, InferenceMode, RetrievalEvent };
export type { GroundedSource } from "./grounded-corpus";
export type { VoiceInputState } from "./speech";
export type { ReadAloudLanguage, ReadAloudState } from "./read-aloud";
